<template>
  <p>November 4th, 2026 by Theo Döllmann</p>
  <h2>Large-Scale Train Delay Predictions</h2>
  <p>
    Travelling by train can be troublesome. Each journey is like gambling. Will you make it? The
    honest answer is not “yes” or “no”, but something like “probably, with a 60 % chance”. Answering
    a question like this is exactly what I have been working on for the past few years, and in this
    post I want to share how I plan to answer it not just for Germany, but for the whole world.
  </p>
  <h3>Some Background</h3>
  <p>Before I start, there are three projects that you need to know about for this post:</p>
  <ul>
    <li>
      <strong><a href="https://bahnvorhersage.de/">Bahn-Vorhersage</a></strong> is a journey planner
      I developed for German railways. It predicts how likely it is that each of your transfers
      works out and ranks journeys by their reliability.
    </li>
    <li>
      <strong><a href="https://transitous.org/">Transitous</a></strong> is a community-run, free and
      open public transport routing service covering large parts of the world. It collects
      timetables (and, where available, realtime data) from hundreds of transit agencies.
    </li>
    <li>
      <strong><a href="https://github.com/motis-project/motis">MOTIS</a></strong> is the open-source
      routing engine that powers Transitous. It is the thing that actually finds your journey from A
      to B.
    </li>
  </ul>
  <p>
    The goal is to bring Bahn-Vorhersage-style reliability predictions to Transitous. Instead of
    just scaling up, I want to use this opportunity to rethink and improve the prediction
    methodology itself.
  </p>
  <p>
    This post is a look into my research and planning process. It is organized into five topics:
  </p>
  <ol>
    <li>Existing Approaches</li>
    <li>Prediction Horizons</li>
    <li>Worldwide Generalization</li>
    <li>Cancellations and Extreme Delays</li>
    <li>Production Setup Considerations</li>
  </ol>
  <h3>A Quick Tour of What Exists</h3>
  <p>
    Before building something new, it is worth looking at what is already out there. There are huge
    number of research papers on train delay prediction, and a growing number of apps that show you
    how likely your transfer is to work out. I will not even try to give a complete picture.
    Instead, here is a hand-picked selection of projects and papers that are relevant to the problem
    I am trying to solve. If you want the full picture of the scientific part of the landscape,
    <a href="#loc-7" role="doc-biblioref">Spanninger et al. (2022)</a> and
    <a href="#loc-9" role="doc-biblioref">Tiong et al. (2023)</a> have both written literature
    reviews.
  </p>
  <p>Let’s start with the projects you can actually use today:</p>
  <ul>
    <li>
      <strong
        ><a
          href="https://reitlinger.notion.site/Wahrscheinlich-Ankommen-a04600890c6142d9a12540ab58c15993"
          >Wahrscheinlich-Ankommen</a
        >, <a href="https://www.zugfinder.net/en/connection">Zugfinder</a> and
        <a href="https://chuuchuu.com/">chuuchuu</a></strong
      >
      show transfer reliabilities based on historical data. As far as I can tell (none of them are
      open source), they look up how often a given transfer has worked in the past.
    </li>
    <li>
      <strong><a href="https://tespace.traines.eu/tstp">TeSpace</a></strong> (<a
        href="#loc-4"
        role="doc-biblioref"
        >Durner 2024</a
      >) is a probabilistic routing engine, mostly for Germany. Instead of the scheduled arrival, it
      optimizes for the <em>expected</em> arrival time, so it can find journeys that are not
      possible according to the timetable but likely work out in practice. It uses delay
      distributions aggregated per train category.
    </li>
    <li>
      <strong>Bahn-Vorhersage</strong> predicts a full delay distribution for every train at every
      transfer with XGBoost and computes transfer reliabilities from them. Tests have shown that
      these reliabilities are almost perfectly calibrated.
    </li>
  </ul>
  <p>
    On the research side, I picked four papers that each tackle a part of my problem.
    <a href="#loc-1">Table 1</a> puts everything side by side.
  </p>
  <div class="table-responsive">
    <table id="loc-1" class="table">
      <thead>
        <tr>
          <th><strong>Project</strong></th>
          <th><strong>Region</strong></th>
          <th><strong>Method</strong></th>
          <th><strong>Long</strong></th>
          <th><strong>Short</strong></th>
          <th><strong>FOSS</strong></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Wahrscheinlich-Ankommen</td>
          <td>DE</td>
          <td>Historical lookup</td>
          <td>✓</td>
          <td>✗</td>
          <td>✗</td>
        </tr>
        <tr>
          <td>Zugfinder</td>
          <td>Many EU</td>
          <td>Historical lookup</td>
          <td>✓</td>
          <td>✗</td>
          <td>✗</td>
        </tr>
        <tr>
          <td>chuuchuu</td>
          <td>Many EU</td>
          <td>Historical lookup</td>
          <td>✓</td>
          <td>✗</td>
          <td>✗</td>
        </tr>
        <tr>
          <td>TeSpace (<a href="#loc-4" role="doc-biblioref">Durner 2024</a>)</td>
          <td>DE</td>
          <td>Aggregated distributions per train category</td>
          <td>✓</td>
          <td>✓</td>
          <td>✓</td>
        </tr>
        <tr>
          <td>Bahn-Vorhersage</td>
          <td>DE</td>
          <td>XGBoost</td>
          <td>✓</td>
          <td>✓</td>
          <td>✓</td>
        </tr>
        <tr>
          <td><a href="#loc-5" role="doc-biblioref">Keyhani et al.</a></td>
          <td>DE</td>
          <td>Historical distributions</td>
          <td>✓</td>
          <td>✓</td>
          <td>✗</td>
        </tr>
        <tr>
          <td><a href="#loc-10" role="doc-biblioref">Wang and Work</a></td>
          <td>US</td>
          <td>Autoregressive</td>
          <td>◐</td>
          <td>◐</td>
          <td>✗</td>
        </tr>
        <tr>
          <td><a href="#loc-6" role="doc-biblioref">Nair et al.</a></td>
          <td>DE</td>
          <td>Ensemble (random forests, simulation, kernel regression)</td>
          <td>◐</td>
          <td>◐</td>
          <td>✗</td>
        </tr>
        <tr>
          <td><a href="#loc-8" role="doc-biblioref">Spanninger et al.</a></td>
          <td>CH</td>
          <td>Neural network</td>
          <td>✗</td>
          <td>✓</td>
          <td>✗</td>
        </tr>
      </tbody>
      <caption>
        Table 1: Existing approaches to predicting delays and transfer reliabilities. ◐ = supported
        with separate models.
      </caption>
    </table>
  </div>
  <p>
    No model-based approach covers more than one country, and only TeSpace and Bahn-Vorhersage
    handle both horizons with a single model. The only multi-region approaches are historical
    lookups, which will not work for Transitous, as we will see.
  </p>
  <h3>Question 1: Can One Model Do Long-Term <em>and</em> Short-Term?</h3>
  <p>
    Think about when you actually care whether a transfer works. Maybe you are booking a trip, weeks
    or even months in advance. Or you are about to leave the house and want to know which of the
    next few connections is the safest bet. Or you are already sitting on a delayed train and wonder
    whether you should replan your journey. These are all the same question, but from a prediction
    point of view they are very different.
  </p>
  <h4>Defining the Horizons</h4>
  <p>To talk about this properly, I use two terms:</p>
  <ul>
    <li>
      <strong>Long-term</strong> predictions are made when there is <em>no</em> information about
      the current state of the railway network. Nobody knows yet whether the train will be late, so
      all we can rely on is what usually happens.
    </li>
    <li>
      <strong>Short-term</strong> predictions are made when we <em>do</em> know something about the
      current state. Either we know the current delay of the train in question, or we know delays of
      other trains that our train might interact with.
    </li>
  </ul>
  <p>
    These are my own definitions. Other authors draw the line differently or call the
    <em>short-term</em> case “online”. So when a paper claims to do long-term prediction, it is
    worth checking what exactly the authors mean.
  </p>
  <h4>How Others Handle It</h4>
  <p>Most approaches pick one of the two scenarios, or build a separate model for each.</p>
  <p>
    <a href="#loc-10" role="doc-biblioref">Wang and Work (2015)</a> built two autoregressive models
    for Amtrak trains in the US, one for each horizon. Compared to the timetable, they reduce the
    prediction error (RMSE) by 12 % and 60 % in the long-term and short-term settings, respectively.
    Those numbers give a feeling for how much more there is to gain once realtime data is available.
  </p>
  <p>
    <a href="#loc-6" role="doc-biblioref">Nair et al. (2019)</a> went much bigger. For Deutsche
    Bahn, they built an ensemble of 22 random forests with over 400 engineered features, together
    with a simulation-based model and a kernel regression model. With 50 million data points, it is
    the largest dataset I have found that wasused in the literature. They also use different models
    for trains that are already running or that are not. They predict up to 24 hours ahead. The
    results show a clear pattern: the further into the future, the smaller the improvement over the
    timetable baseline.
  </p>
  <div class="card shadow my-4">
    <div class="card-body">
      <strong>Note on baselines:</strong> I find the timetable a rather weak baseline. Almost
      anything beats “the train is always on time”. A fairer comparison would be the average delay
      at a stop or, in the short-term case, simply assuming that the current delay stays the same.
    </div>
  </div>
  <p>
    On the deployed side, TeSpace and Bahn-Vorhersage both use a single model for both horizons.
    TeSpace looks up delay distributions based on the train category, the currently predicted delay
    and how far into the future the prediction lies. Bahn-Vorhersage gives the current delay to
    XGBoost as a feature and is explicitly trained with and without it, so the same model can answer
    both kinds of questions.
  </p>
  <h4>How Far Ahead Can We See?</h4>
  <p>
    Why does the advantage shrink so quickly with longer horizons?
    <a href="#loc-8" role="doc-biblioref">Spanninger et al. (2024)</a> offer a nice answer. They
    built an uncertainty-aware neural network for short-term predictions on Swiss railways and split
    its uncertainty into two parts: <em>epistemic</em> uncertainty, which comes from the model not
    knowing better, and <em>aleatoric</em> uncertainty, which comes from the randomness in the data
    itself. As the prediction horizon grows, the epistemic uncertainty stays almost constant, while
    the aleatoric uncertainty rises quickly.
  </p>
  <p>
    In other words: the models are not getting worse; the future is just getting more random. Beyond
    a certain horizon, no model will be able to tell you the exact delay. This is why predicting a
    full probability distribution instead of a single number is so important. If we cannot know the
    delay, we can at least be honest about how uncertain it is.
  </p>
  <h4>My Answer</h4>
  <p>
    I want a single model that can do <em>any-term</em> predictions. It should give a sensible
    answer whether a train leaves in three months or has already left the previous station. To get
    there, I combine two ideas.
  </p>
  <p>
    <strong>Predict the deviation, not the delay.</strong> Instead of predicting the total delay,
    the model predicts how much the actual delay will differ from the operator’s current prediction.
    In the long-term case there is no current prediction, so the model simply predicts the delay. In
    the short-term case, the model learns how far the operator’s prediction can be trusted. Like
    Bahn-Vorhersage today, the model is trained on both cases, so it learns to handle each of them.
  </p>
  <p>
    <strong>Cut the chain when reality catches up.</strong> As I explain in the next section, I plan
    to predict delays segment by segment along the route of a train. Once a train has actually
    arrived at or departed from a stop, there is no need to predict anything up to that point. The
    actual time replaces the predicted distribution, and the predictions start from there.
  </p>
  <p>
    Together, these ideas should give one model that gracefully moves from “this is what usually
    happens” to “this is what is happening right now” as departure gets closer.
  </p>
  <h3>Question 2: How Do You Predict Delays for the Whole World?</h3>
  <p>
    So far, everything I have built for Bahn-Vorhersage was designed for Germany. In Germany, I am
    able to collect all the data from a single <a href="/open-data">data source</a>. The gathering
    process is tedious, but it provides realtime data for all rail services in Germany, and more
    than 90 % of trains have stable train numbers. While I think this is embarrassingly poor-quality
    data for a country like Germany, it is still much better than in many other regions.
  </p>
  <h4>Why Historical Lookups Do Not Work</h4>
  <p>
    The simplest way to estimate whether a transfer works is to look it up: how often did train A
    arrive in time for train B in the past? This is what Wahrscheinlich-Ankommen, Zugfinder and
    chuuchuu do, and it works surprisingly well, as long as you can tell which train is which.
  </p>
  <p>
    In Transitous, you often cannot. Timetables come in formats like GTFS or NeTEx, which do not
    necessarily provide stable IDs for trips. The Swiss feed, for example, has stable IDs; the
    German one does not. The identifier of a trip can change with every timetable update, and train
    numbers are not necessarily included at all. On top of that, lookups fail by design for anything
    new: a new line, a changed timetable or a rerouted service has no history, so there is nothing
    to look up until enough new data has been collected.
  </p>
  <p>
    So instead of asking “how did <em>this</em> train perform?”, we need to ask “how do trains
    <em>like this one</em> perform?” This is exactly what a feature-based model like XGBoost does.
    It does not care about the identity of a trip, only about its properties: the kind of service,
    the stop, the time of day, the position along the route, how long the train has been running,
    and so on. A brand-new train that looks like an existing one gets similar predictions from day
    one.
  </p>
  <h4>Not Every Railway Is the Same</h4>
  <p>
    The second problem is that delay patterns differ a lot between countries, operators and regions.
    A Swiss intercity train, a British commuter train, an American long-distance train and a Chinese
    high-speed train have very little in common. A single model for the whole world needs to be able
    to learn the differences between them.
  </p>
  <p>
    None of the approaches in <a href="#loc-1">Table 1</a> has attempted this, so this is where I
    expect most of the surprises. The good news is that tree-based models like XGBoost are quite
    good at learning separate rules for different groups in the data. Whether that is enough, or
    whether some regions need their own model, is something the data will have to show.
  </p>
  <h4>Missing Realtime Data</h4>
  <p>
    The third problem is realtime coverage. It is missing in many places, but there is no simple
    pattern to where it is missing. Where no historical realtime data is available, I want to be
    transparent and not make any delay predictions.
  </p>
  <p>
    My plan is to make this part of the training process. The training data contains trips with and
    without realtime data. The model’s objective is to predict not only the delay but also whether
    realtime data is available. During inference, the delay prediction is only used when the
    likelihood of missing realtime data is low. This is basically like training an ML-based filter,
    except that it is all done by a single model.
  </p>
  <h4>Following the Path of a Train</h4>
  <p>
    The last idea in this section is the one I am most excited about. Bahn-Vorhersage currently
    predicts the delay at each stop independently. The prediction for the fifth stop of a train
    knows nothing about the prediction for the fourth stop. That is a bit odd, because a train that
    is late at the fourth stop will probably also be late at the fifth stop.
  </p>
  <p>
    Instead of predicting the delay at each stop, I want to predict how much the
    <em>run time</em> between two stops and the <em>dwell time</em> at each stop differ from the
    timetable. The delay distribution at a stop is then the sum of all segment predictions before
    it, as shown in <a href="#loc-2">Figure 1</a>.
  </p>
  <figure class="figure" id="loc-2">
    <svg
      class="figure-img img-fluid"
      viewBox="0 0 349.999992371 51"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      xmlns:h5="http://www.w3.org/1999/xhtml"
    >
      <g clip-path="url(#cC835E5149EA00B9F2B9C5E92B817DA74)">
        <g>
          <image
            xlink:href="data:image/svg+xml;base64,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"
            width="349.999992371"
            height="51"
            preserveAspectRatio="none"
          />
          <g transform="matrix(1 0 0 -1 66.455474854 21.909702301)">
            <use
              xlink:href="#g71286223CC297EFC5D8C8C0FFC2311D"
              x="0"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#gA4FB509ED8775F94D3F977EAEF233EE4"
              x="5.838378906"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g10329CB13BC6EF80680DC4C2A68F612A"
              x="11.155761719"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
          </g>
          <g transform="matrix(1 0 0 -1 13.795192719 29.409700394)">
            <use
              xlink:href="#gE61451AEFB8B6834034D28CB6FDA3D51"
              x="0"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g244043011E1002E3C503445BA1262D"
              x="5.704101563"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#gDC232F8C1D7A6ED37CE2FB8312B6F172"
              x="8.958984375"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g2E2406A59D018FA2C59CAFE0381CA52C"
              x="14.228027344"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g244043011E1002E3C503445BA1262D"
              x="17.848144531"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
          </g>
          <g transform="matrix(1 0 0 -1 169.356964111 21.909702301)">
            <use
              xlink:href="#g71286223CC297EFC5D8C8C0FFC2311D"
              x="0"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#gA4FB509ED8775F94D3F977EAEF233EE4"
              x="5.838378906"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g10329CB13BC6EF80680DC4C2A68F612A"
              x="11.155761719"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
          </g>
          <g transform="matrix(1 0 0 -1 114.331756592 29.409700394)">
            <use
              xlink:href="#gC6853EDDD82C008ECD583456DC371147"
              x="0"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g412D1F611B756371C8B9EE343FD4FC71"
              x="6.278808594"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#gEA915137D1422B065C0CAFA526951A76"
              x="13.427734375"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g86BED2CAEEC19819B13651619C4AF9F0"
              x="18.583984375"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g86BED2CAEEC19819B13651619C4AF9F0"
              x="21.108398438"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
          </g>
          <g transform="matrix(1 0 0 -1 272.258453369 21.909702301)">
            <use
              xlink:href="#g71286223CC297EFC5D8C8C0FFC2311D"
              x="0"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#gA4FB509ED8775F94D3F977EAEF233EE4"
              x="5.838378906"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g10329CB13BC6EF80680DC4C2A68F612A"
              x="11.155761719"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
          </g>
          <g transform="matrix(1 0 0 -1 217.23324585 29.409700394)">
            <use
              xlink:href="#gC6853EDDD82C008ECD583456DC371147"
              x="0"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g412D1F611B756371C8B9EE343FD4FC71"
              x="6.278808594"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#gEA915137D1422B065C0CAFA526951A76"
              x="13.427734375"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g86BED2CAEEC19819B13651619C4AF9F0"
              x="18.583984375"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g86BED2CAEEC19819B13651619C4AF9F0"
              x="21.108398438"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
          </g>
          <g transform="matrix(1 0 0 -1 320.373931885 29.409700394)">
            <use
              xlink:href="#gD3A1B372F9F45AEA3289EC706066F97D"
              x="0"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#g10329CB13BC6EF80680DC4C2A68F612A"
              x="5.467773438"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
            <use
              xlink:href="#gF6A8901FBCE1F52DF6550237074F818"
              x="10.795898438"
              y="0"
              fill="#dee2e6"
              fill-rule="nonzero"
            />
          </g>
        </g>
      </g>
      <defs>
        <symbol id="g71286223CC297EFC5D8C8C0FFC2311D" overflow="visible">
          <path
            d="M 0 0m 4.603027344 0l -1.391113281 3.168945313h -1.375v -3.168945313h -0.98828125v 7.8203125h 2.186035156q 1.165527344 0 1.748291016 -0.588134766q 0.582763672 -0.588134766 0.582763672 -1.759033203q 0 -1.514648438 -1.224609375 -2.078613281l 1.520019531 -3.330078125v -0.064453125h -1.058105469Z m -2.766113281 6.971679688v -2.959472656h 1.187011719q 0.676757813 0 1.015136719 0.427001953q 0.338378906 0.427001953 0.338378906 1.033935547q 0 0.676757813 -0.306152344 1.087646484q -0.306152344 0.410888672 -1.036621094 0.410888672h -1.197753906Z "
          />
        </symbol>
        <symbol id="gA4FB509ED8775F94D3F977EAEF233EE4" overflow="visible">
          <path
            d="M 0 0m 3.700683594 0l -0.016113281 0.510253906q -0.483398438 -0.617675781 -1.385742188 -0.617675781q -0.719726563 0 -1.16015625 0.504882813q -0.440429688 0.504882813 -0.440429688 1.665039063v 3.749023438h 0.9453125v -3.786621094q 0.005371094 -0.768066406 0.260498047 -1.028564453q 0.255126953 -0.260498047 0.555908203 -0.260498047q 0.859375 0 1.192382813 0.778808594v 4.296875h 0.950683594v -5.811523438h -0.90234375Z "
          />
        </symbol>
        <symbol id="g10329CB13BC6EF80680DC4C2A68F612A" overflow="visible">
          <path
            d="M 0 0m 2.755371094 5.081054688q -0.349121094 0 -0.631103516 -0.236328125q -0.281982422 -0.236328125 -0.459228516 -0.617675781v -4.227050781h -0.950683594v 5.811523438h 0.896972656l 0.026855469 -0.617675781q 0.553222656 0.725097656 1.412597656 0.725097656q 0.714355469 0 1.138671875 -0.483398438q 0.424316406 -0.483398438 0.424316406 -1.627441406v -3.808105469h -0.950683594v 3.818847656q 0 0.682128906 -0.225585938 0.972167969q -0.225585938 0.290039063 -0.682128906 0.290039063Z "
          />
        </symbol>
        <symbol id="gE61451AEFB8B6834034D28CB6FDA3D51" overflow="visible">
          <path
            d="M 0 0m 4.264648438 1.9765625q 0 0.547851563 -0.290039063 0.870117188q -0.290039063 0.322265625 -1.240722656 0.663330078q -0.950683594 0.341064453 -1.509277344 0.867431641q -0.55859375 0.526367188 -0.55859375 1.423339844q 0 0.913085938 0.606933594 1.520019531q 0.606933594 0.606933594 1.6328125 0.606933594q 1.144042969 0 1.745605469 -0.725097656q 0.6015625 -0.725097656 0.6015625 -1.638183594h -0.98828125q 0 0.655273438 -0.322265625 1.084960938q -0.322265625 0.4296875 -1.036621094 0.4296875q -0.649902344 0 -0.947998047 -0.362548828q -0.298095703 -0.362548828 -0.298095703 -0.905029297q 0 -0.494140625 0.351806641 -0.821777344q 0.351806641 -0.327636719 1.093017578 -0.596191406q 1.149414063 -0.375976563 1.648925781 -0.934570313q 0.499511719 -0.55859375 0.499511719 -1.471679688q 0 -0.956054688 -0.620361328 -1.525390625q -0.620361328 -0.569335938 -1.678466797 -0.569335938q -0.612304688 0 -1.187011719 0.268554688q -0.574707031 0.268554688 -0.942626953 0.797607422q -0.367919922 0.529052734 -0.367919922 1.307861328h 0.98828125q 0 -0.800292969 0.443115234 -1.165527344q 0.443115234 -0.365234375 1.066162109 -0.365234375q 0.64453125 0 0.977539063 0.335693359q 0.333007813 0.335693359 0.333007813 0.905029297Z "
          />
        </symbol>
        <symbol id="g244043011E1002E3C503445BA1262D" overflow="visible">
          <path
            d="M 0 0m 2.830566406 0q -0.273925781 -0.107421875 -0.666015625 -0.107421875q -0.461914063 0 -0.808349609 0.338378906q -0.346435547 0.338378906 -0.346435547 1.213867188v 3.604003906h -0.886230469v 0.762695313h 0.886230469v 1.412597656h 0.950683594v -1.412597656h 0.875488281v -0.762695313h -0.875488281v -3.609375q 0 -0.445800781 0.153076172 -0.569335938q 0.153076172 -0.123535156 0.346435547 -0.123535156q 0.204101563 0 0.375976563 0.064453125l -0.005371094 -0.811035156Z "
          />
        </symbol>
        <symbol id="gDC232F8C1D7A6ED37CE2FB8312B6F172" overflow="visible">
          <path
            d="M 0 0m 3.706054688 0q -0.091308594 0.209472656 -0.12890625 0.628417969q -0.198730469 -0.295410156 -0.526367188 -0.515625q -0.327636719 -0.220214844 -0.757324219 -0.220214844q -0.762695313 0 -1.221923828 0.4296875q -0.459228516 0.4296875 -0.459228516 1.262207031q 0 0.837890625 0.574707031 1.388427734q 0.574707031 0.550537109 1.71875 0.550537109h 0.655273438v 0.623046875q 0 0.515625 -0.233642578 0.735839844q -0.233642578 0.220214844 -0.700927734 0.220214844q -0.456542969 0 -0.711669922 -0.268554688q -0.255126953 -0.268554688 -0.255126953 -0.633789063h -0.950683594q 0 0.623046875 0.531738281 1.170898438q 0.531738281 0.547851563 1.434082031 0.547851563q 0.805664063 0 1.321289063 -0.402832031q 0.515625 -0.402832031 0.515625 -1.380371094v -2.771484375q 0 -0.821777344 0.177246094 -1.278320313v -0.0859375h -0.982910156Z m -1.267578125 0.757324219q 0.402832031 0 0.698242188 0.209472656q 0.295410156 0.209472656 0.424316406 0.467285156v 1.391113281h -0.499511719q -0.682128906 0 -1.090332031 -0.290039063q -0.408203125 -0.290039063 -0.408203125 -0.832519531q 0 -0.520996094 0.217529297 -0.733154297q 0.217529297 -0.212158203 0.657958984 -0.212158203Z "
          />
        </symbol>
        <symbol id="g2E2406A59D018FA2C59CAFE0381CA52C" overflow="visible">
          <path
            d="M 0 0m 2.685546875 4.962890625q -0.73046875 0 -1.020507813 -0.794921875v -4.16796875h -0.950683594v 5.811523438h 0.918457031l 0.021484375 -0.585449219q 0.381347656 0.692871094 1.079589844 0.692871094q 0.252441406 0 0.375976563 -0.075195313l 0.005371094 -0.923828125q -0.209472656 0.04296875 -0.4296875 0.04296875Z "
          />
        </symbol>
        <symbol id="gC6853EDDD82C008ECD583456DC371147" overflow="visible">
          <path
            d="M 0 0m 0.843261719 0v 7.8203125h 1.863769531q 1.321289063 0 2.10546875 -0.824462891q 0.784179688 -0.824462891 0.784179688 -2.425048828v -1.326660156q 0 -1.600585938 -0.789550781 -2.422363281q -0.789550781 -0.821777344 -2.19140625 -0.821777344h -1.772460938Z m 0.98828125 6.971679688v -6.128417969h 0.784179688q 1.041992188 0 1.517333984 0.593505859q 0.475341797 0.593505859 0.486083984 1.753662109v 1.391113281q 0 1.240722656 -0.47265625 1.815429688q -0.47265625 0.574707031 -1.439453125 0.574707031h -0.875488281Z "
          />
        </symbol>
        <symbol id="g412D1F611B756371C8B9EE343FD4FC71" overflow="visible">
          <path
            d="M 0 0m 6.864257813 5.811523438l -1.41796875 -5.811523438h -0.757324219l -1.138671875 4.157226563l -1.101074219 -4.157226563h -0.762695313l -1.41796875 5.811523438h 0.939941406l 0.90234375 -4.028320313l 1.079589844 4.028320313h 0.751953125l 1.106445313 -4.146484375l 0.880859375 4.146484375h 0.934570313Z "
          />
        </symbol>
        <symbol id="gEA915137D1422B065C0CAFA526951A76" overflow="visible">
          <path
            d="M 0 0m 4.678222656 0.768066406q -0.236328125 -0.333007813 -0.684814453 -0.604248047q -0.448486328 -0.271240234 -1.200439453 -0.271240234q -1.041992188 0 -1.648925781 0.625732422q -0.606933594 0.625732422 -0.612304688 1.941650391v 0.682128906q 0 1.536132813 0.666015625 2.156494141q 0.666015625 0.620361328 1.487792969 0.620361328q 1.090332031 0 1.562988281 -0.703613281q 0.47265625 -0.703613281 0.47265625 -2.035644531v -0.553222656h -3.238769531v -0.139648438q 0 -0.929199219 0.351806641 -1.356201172q 0.351806641 -0.427001953 1.012451172 -0.427001953q 0.478027344 0 0.786865234 0.185302734q 0.308837891 0.185302734 0.545166016 0.475341797l 0.499511719 -0.596191406Z m -1.992675781 4.334472656q -0.494140625 0 -0.821777344 -0.341064453q -0.327636719 -0.341064453 -0.375976563 -1.323974609h 2.282714844v 0.134277344q -0.037597656 0.671386719 -0.249755859 1.101074219q -0.212158203 0.4296875 -0.835205078 0.4296875Z "
          />
        </symbol>
        <symbol id="g86BED2CAEEC19819B13651619C4AF9F0" overflow="visible">
          <path d="M 0 0m 1.734863281 8.25v -8.25h -0.9453125v 8.25h 0.9453125Z " />
        </symbol>
        <symbol id="gD3A1B372F9F45AEA3289EC706066F97D" overflow="visible">
          <path
            d="M 0 0m 0.843261719 0v 7.8203125h 4.151855469v -0.848632813h -3.163574219v -2.513671875h 2.760742188v -0.843261719h -2.760742188v -2.771484375h 3.217285156v -0.843261719h -4.205566406Z "
          />
        </symbol>
        <symbol id="gF6A8901FBCE1F52DF6550237074F818" overflow="visible">
          <path
            d="M 0 0m 3.840332031 0l -0.04296875 0.628417969q -0.461914063 -0.735839844 -1.348144531 -0.735839844q -0.859375 0 -1.399169922 0.676757813q -0.539794922 0.676757813 -0.539794922 2.057128906v 0.542480469q 0 1.434082031 0.537109375 2.092041016q 0.537109375 0.657958984 1.412597656 0.657958984q 0.843261719 0 1.299804688 -0.676757813v 3.0078125h 0.9453125v -8.25h -0.864746094Z m -2.379394531 2.626464844q 0 -0.972167969 0.290039063 -1.431396484q 0.290039063 -0.459228516 0.9453125 -0.459228516q 0.708984375 0 1.063476563 0.719726563v 2.916503906q -0.370605469 0.708984375 -1.052734375 0.708984375q -0.655273438 0 -0.947998047 -0.461914063q -0.292724609 -0.461914063 -0.298095703 -1.412597656v -0.580078125Z "
          />
        </symbol>
      </defs>
      <defs>
        <clipPath id="cC835E5149EA00B9F2B9C5E92B817DA74">
          <path d="M 0 0h 349.999992371v 51h -349.999992371Z " />
        </clipPath>
      </defs>
    </svg>
    <figcaption class="figure-caption">
      Figure 1: Chaining run-time and dwell-time predictions along the route of a train. The delay
      distribution at a stop is the sum of all segment predictions before it.
    </figcaption>
  </figure>
  <p>
    The model becomes <em>path-aware</em>: delays build up along the route instead of appearing out
    of nowhere. It fits the short-term case perfectly, because the chain can be cut wherever real
    times are known (see Question 1). And it generalizes well, because a segment between two stops
    is a much more universal concept than a specific train at a specific stop. I have done some (so
    far unpublished) research on how much path awareness helps a prediction model, and the results
    make me optimistic. At least in Germany, there are some route sections that create a lot more
    delay than others.
  </p>
  <p>
    I have already shown that long-term dwell-time predictions with XGBoost work for German railways
    (<a href="#loc-3" role="doc-biblioref">Döllmann 2026</a>). I have a strong feeling that the same
    should work for run times. <a href="#loc-5" role="doc-biblioref">Keyhani et al. (2012)</a> did
    something very similar: they combined run-time distributions per train and segment with minimum
    dwell times, minimum transfer times and the waiting rules for feeder trains from the German
    timetable. Their reliability ratings turned out to be very usable, though not perfectly
    calibrated, and they suspected that better input distributions would improve them. Better input
    distributions are exactly what a model like XGBoost should provide.
  </p>
  <p>
    There is one simple safeguard I will add: trains do not leave early. Any probability of an early
    departure is moved to zero delay.
  </p>
  <p>
    The biggest open question is how well the individual segment predictions combine. Summing
    distributions assumes the segments are independent, which they are certainly not. A train that
    is slow on one segment is probably still being driven by the same driver, in the same weather,
    on the same busy line on the next one. Small errors in every segment might add up to a large
    error at the end of a long route. How much this matters in practice is one of the first things I
    want to find out.
  </p>
  <h3>Question 3: What About Cancellations and Extreme Delays?</h3>
  <p>
    Many causes of delay are totally random (e.g. police activity, …). It is unlikely that we will
    ever be able to predict delays caused by random factors. Still, these events are part of the
    story, and very often they are the reason a transfer fails.
  </p>
  <h4>The Random Chaotic Problem</h4>
  <p>
    Some of the random factors follow patterns. More people get sick in winter, some train types
    fail more often, and weather is quite predictable a few days in advance. But the sheer number of
    semi-predictable factors makes delays hard to predict. When the buffer times in a schedule are
    tight, the situation becomes almost chaotic. A small delay on one train spreads to the next, and
    from there to the next. We will always find some patterns, but a chaotic system will never be
    very predictable.
  </p>
  <p>
    This matches the research.
    <a href="#loc-8" role="doc-biblioref">Spanninger et al. (2024)</a> showed that the uncertainty
    from randomness dominates as soon as we look a bit into the future.
    <a href="#loc-6" role="doc-biblioref">Nair et al. (2019)</a> note that delays have a long-tailed
    distribution: most trains are on time or a little late, but there is a long tail of very large
    delays, and many of the factors behind them are random. In my experience, the very large delays
    in the long tail of the distribution are influenced by random factors more than smaller delays
    are. So I assume that large delays in general are less predictable than small ones.
  </p>
  <h4>Cancellations are Worse than Delays</h4>
  <p>
    From a passenger’s point of view, a cancelled train is the worst case. Your transfer does not
    just become tight; it disappears. Yet most of the literature focuses on delays and ignores
    cancellations entirely. Predicting both together seems to be a surprisingly under-researched
    topic.
  </p>
  <p>
    My plan is to treat a cancellation as just another possible outcome. The training data contains
    cancelled trips, and the model predicts the probability of a cancellation alongside the
    probabilities of the different delays. The transfer reliability then naturally accounts for both
    delays and cancellations.
  </p>
  <h4>Coarser Buckets for Larger Delays</h4>
  <p>
    Bahn-Vorhersage currently predicts a discrete probability distribution with one class for every
    minute of delay between
    <math>
      <mo>−</mo>
      <mn>3</mn>
    </math>
    and
    <math>
      <mn>30</mn>
    </math>
    minutes. That makes 34 classes, which is a lot, especially for XGBoost, where every additional
    class means more trees. At the same time, the range is not big enough. In Germany, this range
    only covers 97 % of all delays, and in other regions the coverage is probably even smaller.
  </p>
  <p>
    But do we really need minute precision for large delays? If the model cannot know whether a
    train will be 23 or 27 minutes late because the delay was most likely caused by something
    random, there is no point in pretending otherwise. So instead of one class per minute, I want to
    use small buckets for small delays and larger buckets for larger ones. For example the positive
    cases:
  </p>
  <math display="block" class="my-3">
    <mrow>
      <mo>{</mo>
      <mn>0</mn>
      <mo>}</mo>
    </mrow>
    <mo>,</mo>
    <mrow>
      <mo>{</mo>
      <mn>1</mn>
      <mo>}</mo>
    </mrow>
    <mo>,</mo>
    <mrow>
      <mo>{</mo>
      <mn>2</mn>
      <mo>}</mo>
    </mrow>
    <mo>,</mo>
    <mrow>
      <mo>{</mo>
      <mrow>
        <mn>3</mn>
        <mi>…</mi>
        <mn>4</mn>
      </mrow>
      <mo>}</mo>
    </mrow>
    <mo>,</mo>
    <mrow>
      <mo>{</mo>
      <mrow>
        <mn>5</mn>
        <mi>…</mi>
        <mn>7</mn>
      </mrow>
      <mo>}</mo>
    </mrow>
    <mo>,</mo>
    <mrow>
      <mo>{</mo>
      <mrow>
        <mn>8</mn>
        <mi>…</mi>
        <mn>12</mn>
      </mrow>
      <mo>}</mo>
    </mrow>
    <mo>,</mo>
    <mrow>
      <mo>{</mo>
      <mrow>
        <mn>13</mn>
        <mi>…</mi>
        <mn>20</mn>
      </mrow>
      <mo>}</mo>
    </mrow>
    <mo>,</mo>
    <mrow>
      <mo>{</mo>
      <mrow>
        <mn>21</mn>
        <mi>…</mi>
        <mn>40</mn>
      </mrow>
      <mo>}</mo>
    </mrow>
    <mo>,</mo>
    <mrow>
      <mo>{</mo>
      <mrow>
        <mo form="infix" lspace="0em">></mo>
        <mn>40</mn>
      </mrow>
      <mo>}</mo>
    </mrow>
  </math>
  <p>
    The exact bucket boundaries in this example are made up. The real ones will come from the data
    by splitting the delays into buckets that contain roughly the same number of observations. That
    way, every class has enough training data, and the precision ends up where most of the delays
    are.
  </p>
  <h3>Question 4: How Expensive Is This, and Where Should It Run?</h3>
  <p>
    Good predictions are only useful if they arrive in time. Nobody wants to wait a minute for a
    journey planner to tell them whether their transfer works. And at the scale of Transitous, small
    costs add up quickly.
  </p>
  <h4>The Compute Budget</h4>
  <p>
    There are two ways to provide predictions: compute them ahead of time for every train and store
    them, or compute them on demand for the journeys a user actually asks for. Both have a cost.
  </p>
  <p>
    Computing everything ahead of time means predicting a distribution for every stop of every trip
    in the world, and updating it whenever new realtime data comes in. The memory needed grows with
    the number of stop events multiplied by the number of classes.
  </p>
  <p>
    Computing on demand avoids storing all of this, but moves the cost into the request. With the
    segment chain from Question 2, a prediction for one stop needs all segments before it, and a
    journey with several transfers needs predictions for several trains. The cost of a single
    XGBoost prediction grows with the number of trees, and for a classifier that number grows with
    the number of classes.
  </p>
  <p>
    The more the service is used, the more worthwhile the first option becomes. However, when this
    is implemented as a new feature, demand is likely to be low, and on-demand predictions save a
    lot of compute.
  </p>
  <h4>Inside MOTIS or Next to It?</h4>
  <p>
    The most elegant solution would be to build the predictions directly into MOTIS. Then the
    routing itself could take reliability into account. Instead of rating the journeys MOTIS finds,
    it could <em>search</em> for reliable journeys and find alternatives that a timetable-only
    search would never suggest.
    <a href="#loc-5" role="doc-biblioref">Keyhani et al. (2012)</a> showed that this is possible,
    although their integration is long gone from today’s MOTIS codebase.
  </p>
  <p>
    Building the predictions into MOTIS would require computing them ahead of time as described
    above, because the routing touches many more trips than the journey ends up using. I would
    rather first find out what good predictions look like, and only then decide which shortcuts are
    acceptable.
  </p>
  <p>
    So I will start with a separate service. MOTIS routes journeys as it does today, and the service
    applies the delay predictions and rates the reliability of each transfer. To keep things simple
    for apps built on top of Transitous, MOTIS can query the service itself and return the
    reliabilities together with the journeys, so no app has to implement any extra endpoint. Moving
    the predictions into the routing itself remains an option for later, once we know what they cost
    and what they are worth.
  </p>
  <h3>Where Things Stand</h3>
  <p>
    Right now, all of this is shifting from planning to implementation. The first piece is already
    running: I published the
    <a href="https://gitlab.com/bahnvorhersage/transitous_data_archiver">Transitous data archiver</a
    >, which collects and stores the timetable and realtime feeds we will need for training. Next up
    is turning those raw feeds into something a model can actually learn from. I am currently
    building a parser on top of MOTIS to convert the archived GTFS data into a clean tabular format.
    Once that pipeline is in place, the real experiments can begin.
  </p>
  <section role="doc-bibliography" class="hanging-indent">
    <h2>References</h2>
    <ul style="list-style-type: none">
      <li id="loc-3">
        Döllmann, Theo. 2026. “Predicting Train Dwell Times Using Xgboost and Simple Timetable
        Features for German Railways.” Other.
        <a href="https://opus.bibliothek.uni-augsburg.de/opus4/130259"
          >https://opus.bibliothek.uni-augsburg.de/opus4/130259</a
        >.
      </li>
      <li id="loc-4">
        Durner, Robin. 2024. “Stochastic Strategies for Public Transport Journeys Based on Realtime
        Delay Predictions.” Master's thesis, Universität Stuttgart.
        <a href="https://doi.org/10.18419/OPUS-14802">https://doi.org/10.18419/OPUS-14802</a>.
      </li>
      <li id="loc-5">
        Keyhani, Mohammad H., Mathias Schnee, Karsten Weihe, and Hans-Peter Zorn. 2012. “Reliability
        and Delay Distributions of Train Connections.” 25 : 35–46.
        <a href="https://doi.org/10.4230/OASICS.ATMOS.2012.35"
          >https://doi.org/10.4230/OASICS.ATMOS.2012.35</a
        >.
      </li>
      <li id="loc-6">
        Nair, Rahul, Thanh Lam Hoang, Marco Laumanns, et al. 2019. “An Ensemble Prediction Model for
        Train Delays.” <em>Transportation Research Part C: Emerging Technologies</em> 104 (July):
        196–209.
        <a href="https://doi.org/10.1016/j.trc.2019.04.026"
          >https://doi.org/10.1016/j.trc.2019.04.026</a
        >.
      </li>
      <li id="loc-7">
        Spanninger, Thomas, Alessio Trivella, Beda Büchel, and Francesco Corman. 2022. “A Review of
        Train Delay Prediction Approaches.”
        <em>Journal of Rail Transport Planning &amp; Management</em> 22 (June): 100312.
        <a href="https://doi.org/10.1016/j.jrtpm.2022.100312"
          >https://doi.org/10.1016/j.jrtpm.2022.100312</a
        >.
      </li>
      <li id="loc-8">
        Spanninger, Thomas, Nina Wiedemann, and Francesco Corman. 2024. “Quantifying the Dynamic
        Predictability of Train Delay with Uncertainty-Aware Neural Networks.”
        <em>Transportation Research Part C: Emerging Technologies</em> 162 (May): 104563.
        <a href="https://doi.org/10.1016/j.trc.2024.104563"
          >https://doi.org/10.1016/j.trc.2024.104563</a
        >.
      </li>
      <li id="loc-9">
        Tiong, Kah Yong, Zhenliang Ma, and Carl-William Palmqvist. 2023. “A Review of Data-Driven
        Approaches to Predict Train Delays.”
        <em>Transportation Research Part C: Emerging Technologies</em> 148 (March): 104027.
        <a href="https://doi.org/10.1016/j.trc.2023.104027"
          >https://doi.org/10.1016/j.trc.2023.104027</a
        >.
      </li>
      <li id="loc-10">
        Wang, Ren, and Daniel B. Work. 2015. “Data Driven Approaches for Passenger Train Delay
        Estimation.” In “2015 IEEE 18th International Conference on Intelligent Transportation
        Systems.” Special issue,
        <em>2015 IEEE 18th International Conference on Intelligent Transportation Systems</em>,
        September, 535–40.
        <a href="https://doi.org/10.1109/itsc.2015.94">https://doi.org/10.1109/itsc.2015.94</a>.
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
definePageMeta({ layout: 'text-content-page' })

useHead({
  title: 'Blog: Scaling Bahn-Vorhersage to the World using Transitous',
  htmlAttrs: {
    lang: 'en'
  },
  meta: [
    {
      name: 'description',
      content:
        'Blog: How I plan to scale Bahn-Vorhersage to the world using Transitous, and what I have learned about train delay prediction along the way.'
    }
  ]
})
</script>

<style lang="scss" scoped>
p,
div,
li {
  text-align: justify;
}

table {
  text-align: left;
}

.hanging-indent {
  ul {
    list-style-type: none;
    padding-left: 0;
  }

  li {
    text-indent: -1.5em;
    padding-left: 1.5em;
    margin-bottom: 0.5em;
  }
}

/* Alignment */
mtable.right-align mtd,
mtable mtd.right-align,
mtable.left-align mtd.right-align,
mtable.aligned mtd:nth-child(odd) {
  justify-items: end;
  text-align: right;
}

mtable.cases mtd,
mtable.left-align mtd,
mtable mtd.left-align,
mtable.aligned mtd:nth-child(even),
math:is(:not([display])) > mtable.multiline-equation mtd {
  justify-items: start;
  text-align: left;
}

mtable.cases mtd,
mtable.aligned mtd,
mtable mtd.flushed,
mtable mtd.left-flush {
  padding-left: 0;
}

mtable.cases mtd,
mtable.aligned mtd,
mtable mtd.flushed,
mtable mtd.right-flush {
  padding-right: 0;
}

/* Tables */
mtable {
  math-style: inherit;
}

mtd {
  math-depth: auto-add;
  math-style: compact;
  math-shift: compact;
}

/* Equations */
mtable.multiline-equation mtd {
  math-depth: inherit;
  math-style: inherit;
  math-shift: inherit;
  padding: 0;
}

math > mtable.multiline-equation mtr:not(:last-child) mtd {
  padding-bottom: 0.5em;
}

/* Fractions */
mfrac {
  padding-inline: 0;
  margin-inline: 0.1em;
}

/* Accents */
mover[accent='true' i] > :first-child {
  font-feature-settings: 'dtls';
}

mover.dotted[accent='true' i] > :first-child {
  font-feature-settings: 'dtls' 0;
}

/* Other rules for scriptlevel, displaystyle and math-shift */
munder > :nth-child(2),
munderover > :nth-child(2) {
  math-shift: compact;
}

munder[accentunder='true' i] > :not(:first-child),
mover[accent='true' i] > :not(:first-child) {
  math-depth: inherit;
  math-style: inherit;
  math-shift: inherit;
}
</style>
